import { computePosition } from "@floating-ui/dom";
import { unmount } from "svelte";
import { createClassComponent } from "svelte/legacy";
import SuggestionMenu from "#lib/components/content-editors/extensions/SuggestionMenu.svelte";
import { showElement } from "#lib/components/content-editors/extensions/utils.js";
import { Logger } from "#lib/logging.js";
import type { EditorEvents } from "@tiptap/core";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";

export function dropdownRenderer() {
	let wrapper: HTMLDivElement | undefined;
	let component: ReturnType<typeof SuggestionMenu> | undefined;
	let cleanupBlur: (() => void) | undefined;

	function destroyDropdown(source: "blur" | "exit") {
		cleanupBlur?.();
		cleanupBlur = undefined;

		if (!component) {
			wrapper?.remove();
			wrapper = undefined;
			return;
		}

		const current = component;
		component = undefined;

		const internal = (current as unknown as { $$?: { fragment?: unknown } }).$$;
		if (!internal || internal.fragment == null) {
			wrapper?.remove();
			wrapper = undefined;
			return;
		}

		unmount(current).catch(err => Logger.error(`Failed to unmount dropdown component on ${source}`, { err }));
		wrapper?.remove();
		wrapper = undefined;
	}

	return {
		onStart(props: SuggestionProps) {
			if (!props.editor.isInitialized) {
				return;
			}
			wrapper = document.createElement("div");
			document.body.appendChild(wrapper);
			wrapper.classList.add("plaintext-child");
			let anchor = props.editor.view.dom.closest(".suggestion-anchor");
			if (!anchor) {
				anchor = props.editor.view.dom;
			}
			(component as ReturnType<typeof SuggestionMenu>) = createClassComponent({
				component: SuggestionMenu,
				target: wrapper,
				props: { props },
			});

			const dropdown = wrapper;
			if (!dropdown) {
				Logger.error("Dropdown wrapper missing before styling; aborting render");
				destroyDropdown("exit");
				return;
			}

			Object.assign(dropdown.style, {
				position: "absolute",
				zIndex: 100,
				opacity: 0,
				transition: "opacity 0.2s ease-in-out",
				width: anchor.getBoundingClientRect().width + "px",
			});

			function handleBlur(e: EditorEvents["blur"]) {
				// ignore blur event if clicking dropdown
				if (e.event.relatedTarget && wrapper?.contains(e.event.relatedTarget as Element)) {
					return;
				}

				destroyDropdown("blur");
			}

			props.editor.on("blur", handleBlur);
			cleanupBlur = () => {
				if (typeof props.editor.off === "function") {
					props.editor.off("blur", handleBlur);
				}
			};

			computePosition(anchor, dropdown, { placement: "bottom-start" })
				.then(({ x, y }) => {
					if (!dropdown.isConnected) {
						return;
					}
					showElement(dropdown, x, y);
				})
				.catch(err => Logger.error("Failed to compute position for dropdown", { err }));
		},

		onUpdate(props: SuggestionProps) {
			component?.$set?.({ props });
		},

		onExit() {
			destroyDropdown("exit");
		},

		onKeyDown(props: SuggestionKeyDownProps) {
			if (props.event.key === "Escape") {
				return true;
			}
			return component?.onKeyDown?.(props) ?? false;
		},
	};
}
